/**
 * AppliesOpportunities — "Convocatorias que aplican" dentro del documento
 * vivo del Asistente Formulador (slice 1.1).
 *
 * Reusa el mismo matching del Radar: trae hasta 3 convocatorias activas
 * para el municipio (`match.score` + `match.reasons`), ordenadas por
 * relevancia (lo hace el backend), y las muestra como filas compactas con
 * `MatchBadge` + titulo + linea de origen/sector/cierre.
 *
 * Sin deep-link por convocatoria: el Radar (`pages/Radar.tsx`) no expone
 * ruta ni modal por id — solo filtra en memoria sobre `/radar`. Por eso
 * cada fila y el footer enlazan a `/radar` en vez de a un detalle.
 *
 * Segun el expediente (issue #572): filtra por su sector (salvo `generico`,
 * el de los idea-first sin sector fijado) y no lista la convocatoria de la
 * que nacio. Si el sector no tiene convocatorias abiertas, lo dice y cae al
 * top del municipio. Antes todos los expedientes veian la misma lista.
 *
 * Estados:
 * - loading: skeleton minimal (3 barras).
 * - error / sin convocatorias: empty state compacto in-line.
 * - con datos: titulo que dice que lista es + filas + footer "Ver todas en el Radar ->".
 */
import { useQuery } from "@tanstack/react-query";

import { MatchBadge } from "@/components/MatchBadge";
import { fetchOpportunities, type OpportunityDTO } from "@/lib/api/opportunities";
import { sectorLabel } from "@/lib/api/projects";
import { parseIsoDate } from "@/lib/dates";

const FILAS = 3;

interface AppliesOpportunitiesProps {
  municipality: string;
  /** Sector del expediente. `generico` o ausente = sin filtro por sector. */
  sector?: string | null;
  /** Convocatoria de la que nacio el expediente: no se lista a si misma. */
  excludeOpportunityId?: string | null;
}

export function AppliesOpportunities({
  municipality,
  sector,
  excludeOpportunityId,
}: AppliesOpportunitiesProps) {
  const sectorFiltro = sector && sector !== "generico" ? sector : null;
  // El texto nombra el sector con su rotulo, no con la clave (issue #880).
  const rotuloSector = sectorFiltro ? sectorLabel(sectorFiltro) : null;
  // Una fila de mas: si la convocatoria de origen viene en la lista, se saca
  // y igual quedan FILAS.
  const sinLaDeOrigen = (items: OpportunityDTO[]) =>
    items.filter((o) => o.id !== excludeOpportunityId).slice(0, FILAS);

  const porSector = useQuery({
    queryKey: ["opportunities-applies", municipality, sectorFiltro],
    queryFn: () =>
      fetchOpportunities(
        sectorFiltro
          ? { municipality, sector: sectorFiltro, limit: FILAS + 1 }
          : { municipality, limit: FILAS + 1 },
      ),
    enabled: Boolean(municipality),
  });
  const itemsSector = porSector.isError ? [] : sinLaDeOrigen(porSector.data?.items ?? []);
  const sectorVacio = sectorFiltro !== null && !porSector.isLoading && itemsSector.length === 0;

  const delMunicipio = useQuery({
    queryKey: ["opportunities-applies", municipality, null],
    queryFn: () => fetchOpportunities({ municipality, limit: FILAS + 1 }),
    enabled: Boolean(municipality) && sectorVacio,
  });

  if (porSector.isLoading || (sectorVacio && delMunicipio.isLoading)) {
    return (
      <div data-testid="applies-opportunities-skeleton" className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
        ))}
      </div>
    );
  }

  const items = sectorVacio
    ? delMunicipio.isError
      ? []
      : sinLaDeOrigen(delMunicipio.data?.items ?? [])
    : itemsSector;

  if (items.length === 0) {
    return (
      <p data-testid="applies-opportunities-empty" className="text-sm text-slate-500">
        Aún no hay convocatorias abiertas que apliquen. El Radar se actualiza a diario.
      </p>
    );
  }

  return (
    <div data-testid="applies-opportunities" className="space-y-1">
      {sectorVacio && (
        <p className="mb-1 text-sm text-slate-600">
          No hay convocatorias abiertas del sector {rotuloSector} ahora mismo.
        </p>
      )}
      <h3
        data-testid="applies-opportunities-heading"
        className="mb-1 text-sm font-semibold text-slate-900"
      >
        {sectorFiltro && !sectorVacio
          ? `Convocatorias abiertas del sector ${rotuloSector}`
          : "Convocatorias abiertas para tu municipio"}
      </h3>
      {items.map((opp) => (
        <OpportunityRow key={opp.id} opp={opp} />
      ))}
      <a
        href="/radar"
        className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-800 hover:underline"
      >
        Ver todas en el Radar →
      </a>
    </div>
  );
}

function OpportunityRow({ opp }: { opp: OpportunityDTO }) {
  const closingLabel = formatClosing(opp.closes_at, opp.until_quota_exhausted);

  return (
    <a
      href="/radar"
      data-testid="applies-opportunity-row"
      className="flex flex-col gap-1 rounded-lg px-2 py-2 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-3"
    >
      {opp.match && (
        <MatchBadge score={opp.match.score} noSignals={opp.match.reasons.length === 0} />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{opp.title}</p>
        <p className="truncate text-xs text-slate-500">
          {opp.source.name}
          {opp.sector ? ` · ${sectorLabel(opp.sector)}` : ""}
          {closingLabel ? ` · ${closingLabel}` : ""}
        </p>
      </div>
    </a>
  );
}

/**
 * Formatea `closes_at` como "cierra en N dias" / "cierra <fecha>". Sin fecha,
 * una "hasta agotar cupos" (#692) lo dice; las demás no dicen nada.
 */
function formatClosing(closesAt: string | null, untilQuotaExhausted = false): string | null {
  if (!closesAt) return untilQuotaExhausted ? "hasta agotar cupos" : null;
  // `closes_at` es `date` sin hora: parseo local, no UTC (#537).
  const closeDate = parseIsoDate(closesAt);
  if (Number.isNaN(closeDate.getTime())) return null;

  const now = new Date();
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysToClose = Math.ceil((closeDate.getTime() - now.getTime()) / msPerDay);

  if (daysToClose < 0) return null;
  if (daysToClose === 0) return "cierra hoy";
  if (daysToClose === 1) return "cierra mañana";
  if (daysToClose <= 30) return `cierra en ${daysToClose} días`;

  return `cierra ${closeDate.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;
}
